<script setup>
import { computed } from 'vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import { FEATURES_LIST, GUARANTEES } from '../constants';

const props = defineProps({
  controller: { type: Object, required: true },
});

const input =
  'w-full px-3 py-2.5 text-[14px] text-lais-text bg-white border border-lais-border rounded-lais-sm focus:outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary-50 placeholder:text-lais-text-muted transition-all';

const priceSummary = computed(() => {
  const parts = [];
  const { state } = props.controller;
  if (state.transactionType !== 'Aluguel' && state.precoVenda) {
    parts.push(`Venda R$ ${state.precoVenda}`);
  }
  if (state.transactionType !== 'Venda' && state.precoAluguel) {
    parts.push(`Aluguel R$ ${state.precoAluguel}`);
  }
  return parts.join(' · ') || 'Não informado';
});

const onCepInput = value => {
  props.controller.updateState({
    cep: value.replace(/\D/g, '').slice(0, 8),
  });
};

const profileLabel = profile => {
  if (!profile) return '';
  if (!profile.sector) return `${profile.displayName} (perfil próprio)`;
  return `${profile.displayName} — Setor ${profile.sector}`;
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="mx-auto flex w-full max-w-3xl flex-col gap-5">
    <!-- Endereço -->
    <div class="flex flex-col gap-4">
      <p
        class="flex items-center gap-1.5 text-[14px] font-semibold text-lais-text-body"
      >
        <span
          class="i-lucide-map-pin size-4 text-lais-primary"
          aria-hidden="true"
        />
        Endereço
      </p>

      <div class="flex items-end gap-2">
        <LaisField label="CEP" required class="flex-1">
          <input
            :value="controller.state.cep"
            placeholder="01415003"
            inputmode="numeric"
            :class="input"
            @input="onCepInput($event.target.value)"
            @blur="controller.lookupCep()"
          />
        </LaisField>
        <button
          type="button"
          class="mb-1 rounded-lais-sm border border-lais-border px-3 py-2.5 text-[13px] font-medium text-lais-text-body hover:border-lais-primary disabled:opacity-50"
          :disabled="controller.isLookingUpCep.value"
          @click="controller.lookupCep()"
        >
          {{ controller.isLookingUpCep.value ? 'Buscando...' : 'Validar' }}
        </button>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <LaisField label="Estado" required>
          <input
            :value="controller.state.estado"
            placeholder="SP"
            :class="input"
            @input="controller.updateState({ estado: $event.target.value })"
          />
        </LaisField>
        <LaisField label="Cidade" required>
          <input
            :value="controller.state.cidade"
            placeholder="São Paulo"
            :class="input"
            @input="controller.updateState({ cidade: $event.target.value })"
          />
        </LaisField>
        <LaisField label="Bairro" required>
          <input
            :value="controller.state.bairro"
            placeholder="Consolação"
            :class="input"
            @input="controller.updateState({ bairro: $event.target.value })"
          />
        </LaisField>
        <LaisField label="Zona">
          <input
            :value="controller.state.zona"
            placeholder="Sul"
            :class="input"
            @input="controller.updateState({ zona: $event.target.value })"
          />
        </LaisField>
      </div>

      <LaisField label="Logradouro" required>
        <input
          :value="controller.state.endereco"
          placeholder="Rua Bela Cintra"
          :class="input"
          @input="controller.updateState({ endereco: $event.target.value })"
        />
      </LaisField>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <LaisField label="Número" helper="Não exibido publicamente">
          <input
            :value="controller.state.numero"
            placeholder="539"
            :class="input"
            @input="controller.updateState({ numero: $event.target.value })"
          />
        </LaisField>
        <LaisField label="Complemento" helper="Não exibido publicamente">
          <input
            :value="controller.state.complemento"
            placeholder="Apto 12"
            :class="input"
            @input="
              controller.updateState({ complemento: $event.target.value })
            "
          />
        </LaisField>
      </div>
    </div>

    <!-- Campos opcionais -->
    <button
      type="button"
      class="flex w-full items-center justify-between border-y border-lais-border py-3"
      @click="controller.toggleOptional()"
    >
      <span class="text-[14px] font-semibold text-lais-text-body">
        Campos opcionais
      </span>
      <span
        class="i-lucide-chevron-down size-5 text-lais-text-muted transition-transform"
        :class="controller.showOptional.value ? 'rotate-180' : ''"
        aria-hidden="true"
      />
    </button>

    <div v-if="controller.showOptional.value" class="flex flex-col gap-4">
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <LaisField label="IPTU (R$/mês)">
          <input
            type="number"
            min="0"
            :value="controller.state.iptu"
            placeholder="150"
            :class="input"
            @input="controller.updateState({ iptu: $event.target.value })"
          />
        </LaisField>
        <LaisField label="Condomínio (R$)">
          <input
            type="number"
            min="0"
            :value="controller.state.valorCondominio"
            placeholder="800"
            :class="input"
            @input="
              controller.updateState({ valorCondominio: $event.target.value })
            "
          />
        </LaisField>
        <LaisField label="Ano de construção">
          <input
            type="number"
            min="0"
            :value="controller.state.anoConstrucao"
            placeholder="2020"
            :class="input"
            @input="
              controller.updateState({ anoConstrucao: $event.target.value })
            "
          />
        </LaisField>
      </div>

      <LaisField label="URL do vídeo">
        <input
          :value="controller.state.videoUrl"
          placeholder="https://youtu.be/..."
          :class="input"
          @input="controller.updateState({ videoUrl: $event.target.value })"
        />
      </LaisField>
      <LaisField label="Tour virtual">
        <input
          :value="controller.state.tourVirtualUrl"
          placeholder="https://..."
          :class="input"
          @input="
            controller.updateState({ tourVirtualUrl: $event.target.value })
          "
        />
      </LaisField>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <LaisField label="Latitude">
          <input
            :value="controller.state.latitude"
            placeholder="-23.55"
            :class="input"
            @input="controller.updateState({ latitude: $event.target.value })"
          />
        </LaisField>
        <LaisField label="Longitude">
          <input
            :value="controller.state.longitude"
            placeholder="-46.65"
            :class="input"
            @input="controller.updateState({ longitude: $event.target.value })"
          />
        </LaisField>
      </div>

      <div>
        <p class="mb-2 text-[14px] font-semibold text-lais-text-body">
          Características
        </p>
        <div class="grid grid-cols-1 gap-1 sm:grid-cols-2">
          <label
            v-for="item in FEATURES_LIST"
            :key="item.key"
            class="flex cursor-pointer items-center gap-2 rounded-lais-sm px-2 py-1.5 hover:bg-lais-bg-soft"
          >
            <input
              type="checkbox"
              :checked="Boolean(controller.state.features[item.key])"
              class="size-4 accent-lais-primary"
              @change="controller.toggleFeature(item.key)"
            />
            <span class="text-[13px] text-lais-text-body">{{
              item.label
            }}</span>
          </label>
        </div>
      </div>

      <div v-if="controller.state.transactionType !== 'Venda'">
        <p class="mb-2 text-[14px] font-semibold text-lais-text-body">
          Garantias (aluguel)
        </p>
        <div class="grid grid-cols-1 gap-1 sm:grid-cols-2">
          <label
            v-for="item in GUARANTEES"
            :key="item.key"
            class="flex cursor-pointer items-center gap-2 rounded-lais-sm px-2 py-1.5 hover:bg-lais-bg-soft"
          >
            <input
              type="checkbox"
              :checked="Boolean(controller.state.guarantees[item.key])"
              class="size-4 accent-lais-primary"
              @change="controller.toggleGuarantee(item.key)"
            />
            <span class="text-[13px] text-lais-text-body">{{
              item.label
            }}</span>
          </label>
        </div>
      </div>
    </div>

    <!-- Seletor de perfil -->
    <div class="flex flex-col gap-2 rounded-lais bg-lais-bg-soft p-4">
      <p class="text-[14px] font-semibold text-lais-text-body">
        Publicar no perfil
      </p>
      <p
        v-if="controller.isLoadingProfiles.value"
        class="text-[13px] text-lais-text-muted"
      >
        Carregando perfis...
      </p>
      <p
        v-else-if="controller.profilesError.value"
        class="text-[13px] text-lais-danger"
      >
        {{ controller.profilesError.value }}
      </p>
      <p
        v-else-if="!controller.profiles.value.length"
        class="text-[13px] text-lais-text-muted"
      >
        Nenhum perfil de publicação disponível para o seu usuário.
      </p>
      <select
        v-else
        :value="controller.selectedProfileId.value"
        :class="input"
        @change="controller.selectProfile(Number($event.target.value))"
      >
        <option
          v-for="profile in controller.profiles.value"
          :key="profile.id"
          :value="profile.id"
        >
          {{ profileLabel(profile) }}
        </option>
      </select>
    </div>

    <!-- Resumo -->
    <div
      class="flex flex-col gap-2 rounded-lais border border-lais-border-soft bg-white p-4"
    >
      <p class="text-[14px] font-semibold text-lais-text-body">
        Resumo do anúncio
      </p>
      <dl class="grid grid-cols-1 gap-x-6 gap-y-1.5 text-[13px] sm:grid-cols-2">
        <div class="flex justify-between gap-2">
          <dt class="text-lais-text-muted">Título</dt>
          <dd class="truncate text-right font-medium text-lais-text-body">
            {{ controller.state.titulo || '—' }}
          </dd>
        </div>
        <div class="flex justify-between gap-2">
          <dt class="text-lais-text-muted">Tipo</dt>
          <dd class="text-right font-medium text-lais-text-body">
            {{
              controller.state.subTipoImovel ||
              controller.state.tipoImovel ||
              '—'
            }}
          </dd>
        </div>
        <div class="flex justify-between gap-2">
          <dt class="text-lais-text-muted">Transação</dt>
          <dd class="text-right font-medium text-lais-text-body">
            {{ controller.state.transactionType }}
          </dd>
        </div>
        <div class="flex justify-between gap-2">
          <dt class="text-lais-text-muted">Preço</dt>
          <dd class="text-right font-medium text-lais-text-body">
            {{ priceSummary }}
          </dd>
        </div>
        <div class="flex justify-between gap-2">
          <dt class="text-lais-text-muted">Local</dt>
          <dd class="truncate text-right font-medium text-lais-text-body">
            {{ controller.state.bairro
            }}{{
              controller.state.cidade ? ', ' + controller.state.cidade : ''
            }}
          </dd>
        </div>
        <div class="flex justify-between gap-2">
          <dt class="text-lais-text-muted">Fotos</dt>
          <dd class="text-right font-medium text-lais-text-body">
            {{ controller.state.selectedImages.length }}
          </dd>
        </div>
        <div class="flex justify-between gap-2 sm:col-span-2">
          <dt class="text-lais-text-muted">Perfil</dt>
          <dd class="text-right font-medium text-lais-text-body">
            {{ profileLabel(controller.selectedProfile.value) || '—' }}
          </dd>
        </div>
      </dl>
    </div>
  </div>
</template>
